import React from "react";
import Image from "@Components/Image";
import wrapperHOC from "@Utils/wrapperHOC";

const FLAGS_BASE_URL = "https://prod-static-assets.amberstudent.com/assets/flags";
/** The apps' round flag set (the same artwork, as 256px PNGs), which covers territories the SVG set lacks. */
const PNG_FLAGS_BASE_URL = "https://prod-static-assets.amberstudent.com/app/flags-png";

const SUPPORTED_CODES = new Set([
  "ad",
  "ae",
  "af",
  "ag",
  "al",
  "am",
  "ao",
  "ar",
  "at",
  "au",
  "aw",
  "az",
  "ba",
  "bb",
  "bd",
  "be",
  "bf",
  "bg",
  "bh",
  "bi",
  "bj",
  "bm",
  "bn",
  "bo",
  "br",
  "bs",
  "bt",
  "bw",
  "by",
  "bz",
  "ca",
  "cd",
  "cf",
  "cg",
  "ch",
  "ci",
  "cl",
  "cm",
  "cn",
  "co",
  "cr",
  "cu",
  "cv",
  "cw",
  "cy",
  "cz",
  "de",
  "dj",
  "dk",
  "dm",
  "do",
  "dz",
  "ec",
  "ee",
  "eg",
  "eng",
  "er",
  "es",
  "et",
  "eu",
  "fi",
  "fj",
  "fk",
  "fo",
  "fr",
  "ga",
  "gb",
  "gd",
  "ge",
  "gg",
  "gh",
  "gi",
  "gl",
  "gm",
  "gn",
  "gq",
  "gr",
  "gt",
  "gu",
  "gw",
  "gy",
  "hk",
  "hn",
  "hr",
  "ht",
  "hu",
  "id",
  "ie",
  "il",
  "im",
  "in",
  "iq",
  "ir",
  "is",
  "it",
  "je",
  "jm",
  "jo",
  "jp",
  "ke",
  "kg",
  "kh",
  "ki",
  "km",
  "kn",
  "kp",
  "kr",
  "kw",
  "kz",
  "la",
  "lb",
  "lc",
  "li",
  "lk",
  "lr",
  "ls",
  "lt",
  "lu",
  "lv",
  "ly",
  "ma",
  "mc",
  "md",
  "me",
  "mg",
  "mk",
  "ml",
  "mm",
  "mn",
  "mo",
  "mq",
  "mr",
  "ms",
  "mt",
  "mu",
  "mv",
  "mw",
  "mx",
  "my",
  "mz",
  "na",
  "ne",
  "ng",
  "ni",
  "nl",
  "no",
  "np",
  "nr",
  "nz",
  "om",
  "pa",
  "pe",
  "pf",
  "pg",
  "ph",
  "pk",
  "pl",
  "pr",
  "ps",
  "pt",
  "pw",
  "py",
  "qa",
  "ro",
  "rs",
  "ru",
  "rw",
  "sa",
  "sb",
  "sc",
  "sct",
  "sd",
  "se",
  "sg",
  "si",
  "sk",
  "sl",
  "sm",
  "sn",
  "so",
  "sr",
  "ss",
  "sv",
  "sx",
  "sy",
  "sz",
  "tc",
  "td",
  "tg",
  "th",
  "tj",
  "tk",
  "tl",
  "tm",
  "tn",
  "to",
  "tr",
  "tt",
  "tv",
  "tw",
  "tz",
  "ua",
  "ug",
  "us",
  "uy",
  "uz",
  "va",
  "vc",
  "ve",
  "vi",
  "vn",
  "vu",
  "wls",
  "ws",
  "xk",
  "ye",
  "za",
  "zm",
  "zw",
]);

interface CountryFlagProps {
  countryCode: string;
  className?: string;
  width?: number | string;
  height?: number | string;
}

/** In the apps' PNG set only (checked against the CDN). */
const PNG_ONLY_CODES = new Set([
  "ai",
  "an",
  "aq",
  "as",
  "ax",
  "bl",
  "cc",
  "ck",
  "cx",
  "fm",
  "gf",
  "gp",
  "gs",
  "io",
  "ky",
  "mf",
  "mh",
  "mp",
  "nc",
  "nf",
  "nu",
  "pm",
  "pn",
  "re",
  "sh",
  "sj",
  "st",
  "vg",
  "wf",
  "yt",
]);

/** Territories in neither set, shown with the flag they officially fly. */
const FLAG_ALIASES: Record<string, string> = {
  bq: "nl", // Caribbean Netherlands
  bv: "no", // Bouvet Island
  hm: "au", // Heard Island and McDonald Islands
  tf: "fr", // French Southern and Antarctic Lands
  um: "us", // United States Minor Outlying Islands
};

const CountryFlag: React.FC<CountryFlagProps> = ({
  countryCode,
  className,
  width = 24,
  height = 24,
}) => {
  const iso = countryCode?.toLowerCase();
  const code = (iso && FLAG_ALIASES[iso]) || iso;

  let src = "";
  if (code && SUPPORTED_CODES.has(code)) src = `${FLAGS_BASE_URL}/${code}.svg`;
  else if (code && PNG_ONLY_CODES.has(code))
    src = `${PNG_FLAGS_BASE_URL}/${code.toUpperCase()}.png`;
  if (!src) return null;

  return (
    <Image src={src} alt={`${code} flag`} width={width} height={height} className={className} />
  );
};

export default wrapperHOC(CountryFlag, {
  componentName: "CountryFlag",
  showForChina: true,
});
